import { CancelledError, type QueryClient, type QueryKey } from '@tanstack/vue-query';
import { errAsync, okAsync, ResultAsync } from 'neverthrow';
import { ApiError } from '~~/types/ApiError';

export type GetQueryDataOrPrefetchOptions<T> = {
	queryClient: QueryClient;
	queryKey: QueryKey;
	queryFn: () => Promise<T>;
	ignoreSentry?: boolean;
	retry?: boolean;
};

export const getQueryDataOrPrefetch = <T>({
	queryClient, queryKey, queryFn, ignoreSentry, retry,
}: GetQueryDataOrPrefetchOptions<T>): ResultAsync<T, Error> => {
	const state = queryClient.getQueryState<T>(queryKey);

	const hasValidData = state
		&& state.status === 'success'
		&& state.data !== undefined
		&& !state.isInvalidated;

	if (hasValidData) {
		return okAsync(state.data as T);
	}

	const runFetch = () => ResultAsync.fromPromise(
		queryClient.query({
			queryKey,
			queryFn,
			meta: {
				ignoreSentry,
			},
			retry: (failureCount, error) => {
				if (retry === false) {
					return false;
				}

				if (error instanceof ApiError && error.status === 404) {
					return false;
				}

				return failureCount < 2;
			},
		}),
		(error) => error instanceof Error ? error : new Error (String(error)),
	);

	return runFetch().orElse((error) =>
		error instanceof CancelledError ? runFetch() : errAsync(error),
	);
};
